import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { PlusIcon } from '../../components/ui/icons';
import { useDetailInView } from '../../components/ui/useDetailInView';
import { useAuth } from '../auth/AuthContext';
import { ComponentPicker } from '../components/ComponentPicker';
import { ARCHIVED_FILTERS, type ArchivedFilter, type ComponentBrief } from '../components/api';
import type { Focus } from '../layout/focus';
import { useSelectedId } from '../layout/useSelectedId';
import { SpecPanel } from './SpecPanel';
import { createSpec, getVersion, listSpecs } from './api';

/**
 * Specifications on the left, the open one on the right.
 *
 * The list shows the state of the newest version rather than the specification's
 * own name for a state, because a BOM does not have one: what matters is whether
 * there is an open draft to work on and what the last released version was.
 */
export function SpecsPage({ focusVersion }: { focusVersion?: Focus | null }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const queryClient = useQueryClient();

  // Like projects and components, the selected specification belongs in the
  // address. A component opened from its BOM then leaves a history entry that
  // can return to this exact specification instead of an empty list.
  const [selectedId, setSelectedId] = useSelectedId('/specs');
  const [creating, setCreating] = useState(false);

  const [archived, setArchived] = useState<ArchivedFilter>('hide');
  const specs = useQuery({
    queryKey: ['specs', 'list', archived],
    queryFn: () => listSpecs(archived),
  });

  // A scanned kit label names a version, not a specification, so the version has
  // to be read before the screen knows which document to open.
  const scanned = useQuery({
    queryKey: ['spec-versions', focusVersion?.id],
    queryFn: () => getVersion(focusVersion?.id as string),
    enabled: Boolean(focusVersion?.id),
  });

  useEffect(() => {
    if (!focusVersion || !scanned.data) return;
    setSelectedId(scanned.data.specification_id);
    setCreating(false);
  }, [focusVersion, scanned.data, setSelectedId]);

  const detail = useDetailInView(`${selectedId}/${creating}`);

  const showingDetail = Boolean(selectedId || creating);

  return (
    <div className={`purchases${showingDetail ? ' is-detail' : ''}`}>
      <aside className="purchases__list">
        <div className="components__search">
          <strong>{t('nav.specifications')}</strong>
          {can('spec.draft.write') && (
            <button
              type="button"
              className="components__icon"
              title={t('specs.add')}
              aria-label={t('specs.add')}
              onClick={() => {
                setCreating(true);
                setSelectedId(null);
              }}
            >
              <PlusIcon />
            </button>
          )}
        </div>

        {/* Hidden by default, mixed in on request, or on their own — the last
            is what makes the archive a place to look through. */}
        <select
          value={archived}
          onChange={(event) => setArchived(event.target.value as ArchivedFilter)}
        >
          {ARCHIVED_FILTERS.map((value) => (
            <option key={value} value={value}>
              {t(`common.archivedFilter.${value}`)}
            </option>
          ))}
        </select>

        {specs.isLoading && <p className="panel__hint">{t('common.loading')}</p>}
        {specs.data?.length === 0 && <p className="panel__hint">{t('specs.empty')}</p>}

        <ul className="components__results">
          {(specs.data ?? []).map((spec) => {
            const draft = spec.versions.find((row) => row.status === 'draft');
            const released = [...spec.versions]
              .reverse()
              .find((row) => row.status === 'released');
            return (
              <li key={spec.id}>
                <button
                  type="button"
                  className={[spec.id === selectedId ? 'is-selected' : '', spec.archived_at ? 'is-archived' : ''].filter(Boolean).join(' ') || undefined}
                  onClick={() => {
                    setSelectedId(spec.id);
                    setCreating(false);
                  }}
                >
                  <span className="components__name">{spec.name}</span>
                  <span className="components__meta">
                    {spec.archived_at && (
                      <span className="badge badge--warn">{t('common.archived')}</span>
                    )}
                    {spec.code && <span className="components__code">{spec.code}</span>}
                    {released && <em>{t('specs.releasedVersion', { version: released.version })}</em>}
                    {draft && (
                      <em className="spec__badge spec__badge--draft">
                        {t('specs.draftVersion', { version: draft.version })}
                      </em>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </aside>

      <div className="purchases__detail" ref={detail}>
        <button
          type="button"
          className="pane-back"
          onClick={() => {
            setSelectedId(null);
            setCreating(false);
          }}
        >
          ← {t('common.backToList')}
        </button>

        {creating ? (
          <NewSpecForm
            onCreated={(id) => {
              setCreating(false);
              setSelectedId(id);
              void queryClient.invalidateQueries({ queryKey: ['specs'] });
            }}
            onCancel={() => setCreating(false)}
          />
        ) : selectedId ? (
          <SpecPanel
            key={selectedId}
            specId={selectedId}
            onArchived={() => setSelectedId(null)}
          />
        ) : (
          <p className="panel__hint">{t('specs.selectHint')}</p>
        )}
      </div>
    </div>
  );
}

/** A new specification opens its first draft straight away — a BOM with no
 *  version to put lines in would be a name and nothing else. */
function NewSpecForm({
  onCreated,
  onCancel,
}: {
  onCreated: (id: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [outputComponent, setOutputComponent] = useState<ComponentBrief | null>(null);

  const create = useMutation({
    mutationFn: (input: { name: string; code: string; description: string; source: string; outputComponentId: string | null }) =>
      createSpec({
        name: input.name,
        code: input.code || null,
        description: input.description || null,
        source_url: input.source || null,
        output_component_id: input.outputComponentId,
      }),
    onSuccess: (spec) => onCreated(spec.id),
    onError: (exc) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  return (
    <form
      className="component-form"
      onSubmit={(event) => {
        event.preventDefault();
        const form = new FormData(event.currentTarget);
        create.mutate({
          name: String(form.get('name')),
          code: String(form.get('code') ?? ''),
          description: String(form.get('description') ?? ''),
          source: String(form.get('source_url') ?? ''),
          outputComponentId: outputComponent?.id ?? null,
        });
      }}
    >
      <h2>{t('specs.new')}</h2>

      <label>
        {t('specs.name')}
        <input name="name" required autoFocus />
      </label>

      {/* The designation the workshop already uses on paper. Not generated: a
          product usually arrives with a name of its own, and inventing a second
          one would mean two names for the same board. */}
      <label>
        {t('specs.code')}
        <input name="code" placeholder={t('specs.codePlaceholder')} />
      </label>

      {/* Where it came from. A BOM is transcribed, not invented, and months
          later "from what, exactly" is the question that comes back. */}
      <label>
        {t('specs.sourceUrl')}
        <input name="source_url" type="url" placeholder="https://" />
      </label>

      <ComponentPicker
        label={t('specs.output')}
        value={outputComponent}
        onChange={setOutputComponent}
      />

      <label>
        {t('specs.description')}
        <textarea name="description" rows={6} />
        <span className="component-form__parsed">{t('common.markdownHint')}</span>
      </label>

      {error && (
        <p className="panel__error">{t(`specs.errors.${error}`, t('specs.errors.unknown'))}</p>
      )}

      <div className="component-form__actions">
        <button type="submit" disabled={create.isPending}>
          {t('common.save')}
        </button>
        <button type="button" onClick={onCancel}>
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
}
